import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import OperatorHit from 'components/OperatorHit';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import Autosuggest, { RenderInputComponentProps } from 'react-autosuggest';

import { List, compact, unionBy } from 'lodash';

import { usePrevious } from 'lib/hooks';
import searchClient, {
  AnySearchFormProps,
  EventsClient,
  OperatorTruckIndexRecord,
  SearchFormHandle,
  SearchIndexResult,
  SearchOptions,
  TransformHitRecordFn,
  parseTimestampsFields,
  replaceEmployeesWithOwners,
} from 'lib/search';
import { typedForwardRef } from 'lib/utils';

import SearchInput from './SearchInput';
import SearchResultsList from './SearchResultsList';

const MIN_SEARCH_TEXT_LENGTH = 1;
const TYPING_DELAY_MS_DEFAULT = 0;
const MAX_RESULTS_WITH_REPLACE_EMPLOYEE = 10;
const QUERY_STRING_WILDCARD = '*';

// fake SearchIndexResult to use as suggestion when
// search result is empty and render no results dialog instead.
const noSearchResults = [{ objectID: null }];

function SearchForm<T extends SearchIndexResult>(
  props: AnySearchFormProps<T>,
  ref: React.ForwardedRef<SearchFormHandle<T>>
) {
  const client = searchClient<T>(props);
  const eventsClient = EventsClient(props);
  const [isClientInitialized, setIsClientInitialized] = useState(false);
  const [searchText, setSearchText] = useState<string>(
    props.initialSearchText || ''
  );
  const inputText = useRef(props.initialSearchText || '');
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const originalSearchResults = useRef<T[]>([]);
  const previousSearchResults = usePrevious(searchResults);
  const previousExcludeElements = usePrevious(props.excludeElements);
  const [pagesLoaded, setPagesLoaded] = useState<number>(0);
  const [shouldHighlightFirstSuggestion, setShouldHighlightFirstSuggestion] =
    useState(true);
  const [preloadResults, setPreloadResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  // After clicking a search result this flag should be turned on and
  // when focusing on input again it should be turned off.
  const [hasClickedResult, setHasClickedResult] = useState(false);
  const [hasClearedResults, setHasClearedResults] = useState(false);
  const isInputEmpty = useRef(true);
  const [currentPage, setCurrentPage] = useState<number>(
    props.currentPage ?? 0
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Add Imperative method to obtain all results for current query
  // from parent component without limit
  useImperativeHandle(
    ref,
    () => ({
      getAllCurrentResults() {
        return client
          .search(searchText, {
            companiesFilter: props.companiesFilter,
            readerCompaniesFilter: props.readerCompanyId,
            aroundCoordinates: props.aroundCoordinates,
            searchFilters: props.searchFilters,
            mandatoryAttributes: props.mandatoryAttributes,
            conjunctiveAttributes: props.conjunctiveAttributes,
            driversOnly: props.driversOnly,
            excludeElements: props.excludeElements,
            hitsPerPage: Number.MAX_SAFE_INTEGER,
          })
          .then((results) => parseAndFilterResults(results, searchText));
      },
      loadNextPage() {
        loadNextPageResults({ page: currentPage + 1 });
      },
      loadPreviousPage() {
        loadNextPageResults({ page: currentPage - 1 });
      },
    }),
    [
      searchText,
      props.companiesFilter,
      props.readerCompanyId,
      props.aroundCoordinates,
      props.searchFilters?.join(),
      props.mandatoryAttributes,
      props.conjunctiveAttributes,
      props.driversOnly,
      props.excludeElements,
      currentPage,
      searchResults,
    ]
  );

  useEffect(() => {
    if (
      props.currentPage !== undefined &&
      props.currentPage !== currentPage &&
      !isLoading
    ) {
      loadNextPageResults({ page: props.currentPage });
    }
  }, [props.currentPage, currentPage, isLoading]);

  useEffect(() => {
    if (!isClientInitialized) {
      client.validateIndex().then(() => setIsClientInitialized(true));
    }
  }, []);

  useEffect(() => {
    if (isClientInitialized && props.showResultsOnLoad) {
      performSearchDebounced(searchText);
    }
  }, [isClientInitialized]);

  useEffect(() => {
    if (props.showResultsOnFocus && !preloadResults.length) {
      performSearchDebounced(QUERY_STRING_WILDCARD, { preload: true });
    }
  }, [props.showResultsOnFocus]);

  useEffect(() => {
    if (!!props.onChangeResults) {
      props.onChangeResults(searchResults);
    }
  }, [searchResults]);

  const cleanup = () => {
    setSearchResults([]);
    setPagesLoaded(0);
  };

  useEffect(() => {
    isInputEmpty.current = inputText.current.length < MIN_SEARCH_TEXT_LENGTH;
    if (isInputEmpty.current && !props.showResultsOnLoad) {
      setIsLoading(false);
      cleanup();
    } else {
      setIsLoading(true);
      performSearchDebounced(searchText);
    }

    return !!props.clearResultsOnTyping ? cleanup : noop;
  }, [
    searchText,
    props.searchFilters?.join(),
    props.mandatoryAttributes?.length,
    props.conjunctiveAttributes?.length,
    props.aroundCoordinates?.lat,
    props.aroundCoordinates?.lng,
  ]);

  // Update search results when excludeElements gets updated (e.g. after selecting a result)
  useEffect(() => {
    if (!props.excludeElements?.length && !props.quickPickSuggestions?.length) {
      return;
    }
    let filteredResults = searchResults;
    // Don't merge quick-pick suggestions if search text is not default one
    if (inputText.current === QUERY_STRING_WILDCARD) {
      filteredResults = mergeQuickPickSuggestions(searchResults);
    }
    // Check if should restore previously excluded results when exclude elements is decreased
    if (
      !!previousExcludeElements?.length &&
      !!props.excludeElements &&
      props.excludeElements.length < previousExcludeElements.length &&
      !!previousSearchResults &&
      previousSearchResults.length > searchResults.length &&
      !hasClearedResults
    ) {
      filteredResults = originalSearchResults.current;
    }
    const idsToExclude = props.excludeElements?.map((o) => o.objectID);
    if (!!idsToExclude?.length) {
      filteredResults = filteredResults.filter((result) => {
        return !idsToExclude.includes(result.objectID);
      });
      if (filteredResults.length !== searchResults.length) {
        setSearchResults(filteredResults);
      }
    }
  }, [
    (props.excludeElements || []).length,
    (props.quickPickSuggestions || []).length,
    searchResults.length,
  ]);

  // Debounce callback to not perform search until user has stopped typing
  const { typingTimeoutMs = TYPING_DELAY_MS_DEFAULT } = props;
  const performSearchDebounced = useCallback(
    debounce(performSearch, typingTimeoutMs),
    [
      typingTimeoutMs,
      props.searchFilters?.join(),
      props.mandatoryAttributes?.length,
      props.conjunctiveAttributes?.length,
      props.aroundCoordinates?.lat,
      props.aroundCoordinates?.lng,
      (props.excludeElements || []).length,
      (props.quickPickSuggestions || []).length,
      preloadResults.length,
    ]
  );

  const isExplicitlyExcludingEmployees = useMemo(() => {
    const roleFilters = props.searchFilters?.filter((filter) =>
      filter.startsWith('operator.role')
    );
    return (
      !!roleFilters?.length &&
      !roleFilters.some((filter) => filter.includes('operatorEmployee'))
    );
  }, [props.searchFilters?.join()]);

  const shouldReplaceEmployees = useCallback(
    (queryString: string) => {
      return !!props.replaceEmployeeWithOwner;
    },
    [props.replaceEmployeeWithOwner]
  );

  function performSearch(queryString: string, options?: { preload: boolean }) {
    // TODO: Following condition is never met as preloadResults is always empty
    if (
      !options?.preload &&
      !!preloadResults.length &&
      queryString === QUERY_STRING_WILDCARD
    ) {
      setIsLoading(false);
      setSearchResults(preloadResults);
      setPreloadResults([]);
      return;
    }
    const searchOptions: SearchOptions<T> = {
      page: props.currentPage ?? 0,
      companiesFilter: props.companiesFilter,
      readerCompaniesFilter: props.readerCompanyId,
      aroundCoordinates: props.aroundCoordinates,
      searchFilters: props.searchFilters,
      mandatoryAttributes: props.mandatoryAttributes,
      conjunctiveAttributes: props.conjunctiveAttributes,
      driversOnly: props.driversOnly,
      excludeElements: props.excludeElements,
      hitsPerPage: props.pageSize,
      includeDeleted: props.includeDeleted,
    };

    if (shouldReplaceEmployees(queryString)) {
      if (queryString !== QUERY_STRING_WILDCARD) {
        searchOptions.hitsPerPage = MAX_RESULTS_WITH_REPLACE_EMPLOYEE;
      }
      // Even if operator employee role is explicitly excluded include them in the filters
      // if replaceEmployeeWithOwner callback is present to be able to replace them
      // with their respective owners
      if (isExplicitlyExcludingEmployees) {
        searchOptions.searchFilters = searchOptions.searchFilters?.concat(
          'operator.role:"operatorEmployee"'
        );
      }
    }
    client.search(queryString, searchOptions).then(async (results) => {
      if (props.enableLogging) {
        console.log(
          `Results for "${queryString}", current text is "${inputText.current}":`,
          results
        );
      }
      // If search promise resolves after the input text has changed
      // or was cleared then do not update the search results
      if (
        inputText.current !== queryString ||
        (isInputEmpty.current &&
          !props.showResultsOnLoad &&
          !props.showResultsOnFocus)
      ) {
        return;
      }
      let filteredResults = results;
      if (queryString === QUERY_STRING_WILDCARD) {
        // Merge quick pick suggestions, if provided, with search results
        filteredResults = mergeQuickPickSuggestions(results);
      }
      const parsedResults = await parseAndFilterResults(
        filteredResults,
        queryString
      );

      originalSearchResults.current = parsedResults;
      if (options?.preload) {
        setPreloadResults(parsedResults);
      } else {
        setSearchResults(parsedResults);
      }
      setCurrentPage(props.currentPage ?? 0);
      setPagesLoaded((props.currentPage ?? 0) + 1);
      setShouldHighlightFirstSuggestion(true);
      setIsLoading(false);
    });
  }

  async function parseAndFilterResults(
    results: T[],
    queryString: string
  ): Promise<T[]> {
    let filteredResults = results;
    // Check if operator employees should be replaced with owners
    if (
      shouldReplaceEmployees(queryString) &&
      !!props.replaceEmployeeWithOwner
    ) {
      // In this situation we know we're dealing with OperatorTruckIndexRecord elements
      // so it's safe to cast generic type T to OperatorTruckIndexRecord
      filteredResults = (await replaceEmployeesWithOwners(
        filteredResults as unknown as OperatorTruckIndexRecord[],
        props.replaceEmployeeWithOwner as unknown as TransformHitRecordFn<OperatorTruckIndexRecord>,
        isExplicitlyExcludingEmployees
      )) as unknown as T[];
    }
    // Check if there are some results to exclude
    if (!!props.excludeElements) {
      const idsToExclude = props.excludeElements.map((o) => o.objectID);
      filteredResults = filteredResults.filter((result) => {
        return !idsToExclude.includes(result.objectID);
      });
    }
    // Check if results should be parsed
    let parsedResults = filteredResults;
    if (!!props.parseSearchResults) {
      parsedResults = await Promise.all(
        parsedResults.map(props.parseSearchResults)
      );
    }
    const timestampsFields = props.timestampsFields;
    if (!!timestampsFields) {
      parsedResults = parsedResults.map((result) =>
        parseTimestampsFields(result, timestampsFields)
      );
    }
    return parsedResults;
  }

  function mergeQuickPickSuggestions(results: List<T> | null | undefined) {
    // Merge quick pick suggestions, if provided, with search results
    const quickPickSuggestions = props.quickPickSuggestions;
    let filteredResults = unionBy(
      compact(quickPickSuggestions),
      results,
      'objectID'
    );
    return filteredResults;
  }

  const getOnClickHandler = (searchResult: T, index: number) => {
    return function onClickHandler() {
      if (!searchResult.objectID) {
        return;
      }
      if (!props.disableAnalytics) {
        eventsClient.sendResultSelectedEvent(searchResult, index + 1);
      }
      setHasClickedResult(true);
      props.onSelectSearchResult?.(searchResult);
    };
  };

  const isOverlayOpen = useMemo(
    () =>
      // Check if should hide overlay after clicking result
      (!props.hideOverlayOnClickResult || !hasClickedResult) &&
      (searchText.length >= MIN_SEARCH_TEXT_LENGTH ||
        !!props.showResultsOnLoad ||
        // Check if overlay should open after focusing
        (!hasClickedResult &&
          props.showResultsOnFocus &&
          (isLoading || !!searchResults.length))),
    [
      searchText,
      props.showResultsOnLoad,
      hasClickedResult,
      isLoading,
      searchResults.length,
      props.showResultsOnFocus,
    ]
  );

  function onChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const text = event.target.value;
    onChangeSearchText(text);
  }

  function onChangeSearchText(text: string) {
    if (text === inputText.current) return;
    setIsLoading(true);
    setSearchText(text);
    inputText.current = text;
    if (props.onChangeSearchText) {
      props.onChangeSearchText(text);
    }
  }

  const onFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    if (hasClearedResults) {
      return;
    }
    // Turn flag off so results overlay can be opened again
    setHasClickedResult(false);
    if (!searchText && !searchResults.length && props.showResultsOnFocus) {
      // Perform a search using wildcard to get any result
      onChangeSearchText(QUERY_STRING_WILDCARD);
    }
    // Auto select search text on focus (unless explicitly disabled)
    if (!props.disableSelectOnFocus) {
      e.persist();
      setTimeout(() => selectSearchText(e), 10);
    }
  };

  function onBlur() {
    // Turn flag off so results overlay can be opened again on next focus
    setHasClearedResults(false);
  }

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const scrollContainer = e.target as HTMLDivElement;
    const { scrollHeight, scrollTop, clientHeight } = scrollContainer;
    const scrollDiff = Math.round(scrollHeight - scrollTop);
    // Check if has scrolled to the end of suggestions list and load next page
    const hasScrolledToBottom = Math.abs(scrollDiff - clientHeight) <= 5;
    if (!!clientHeight && hasScrolledToBottom && !!loadNextPageResults) {
      loadNextPageResults()?.then(() => {
        setTimeout(() => {
          // Scroll back to last item position
          scrollContainer.scrollTo({ top: scrollTop });
        }, 10);
      });
    }
  }

  function onClearSearch() {
    setSearchText('');
    inputText.current = '';
    setHasClearedResults(true);
    setSearchResults([]);
    if (props.showResultsOnLoad) {
      setIsLoading(true);
      performSearchDebounced(searchText);
    }
    if (props.onChangeSearchText) {
      props.onChangeSearchText('');
    }
  }

  function selectSearchText(ev: React.FormEvent) {
    const inputEl = ev.target as HTMLInputElement;
    inputEl.select();
  }

  const loadNextPageResults = useCallback(
    debounce(loadNextPageResultsNotDebounced, typingTimeoutMs),
    [searchResults, searchText, pagesLoaded, currentPage]
  );

  function loadNextPageResultsNotDebounced(options?: { page?: number }) {
    return new Promise((resolve) => {
      const { page } = options || {};
      const isPaging = typeof page === 'number';

      // Check that first page was already loaded and that last page has not been reached
      const [lastResult] = searchResults.slice(-1);
      const lastPage = lastResult?._rankingInfo?.totalPages || 0;
      const pageToLoad = page ?? pagesLoaded;

      if (
        pagesLoaded > 0 &&
        pageToLoad < lastPage &&
        (isPaging ? pageToLoad !== currentPage : true)
      ) {
        setShouldHighlightFirstSuggestion(false);
        setIsLoading(true);
        setIsLoadingNextPage(true);
        client.loadNextPage(pageToLoad).then(async (nextPageResults) => {
          setIsLoading(false);
          setIsLoadingNextPage(false);
          if (!nextPageResults.length) return;
          const parsedResults = await parseAndFilterResults(
            nextPageResults,
            searchText
          );

          if (isPaging) {
            setSearchResults(parsedResults);
            originalSearchResults.current = parsedResults;
            setCurrentPage(pageToLoad);
            setPagesLoaded(pageToLoad + 1);
          } else {
            setPagesLoaded(pageToLoad + 1);
            // Merge new page results with currently loaded ones
            const searchResultsUpdated = unionBy(
              searchResults,
              parsedResults,
              'objectID'
            );
            originalSearchResults.current = searchResultsUpdated;
            setSearchResults(searchResultsUpdated);
          }
          resolve(parsedResults);
        });
      } else {
        resolve(null);
      }
    });
  }

  const inputPlaceholder = props.inputPlaceholder || 'Search';

  const renderInput = (inputProps?: RenderInputComponentProps) =>
    !!props.renderSearchInput ? (
      props.renderSearchInput({
        searchText: searchText,
        searchResults: searchResults,
        inputPlaceholder: inputPlaceholder,
        onChangeHandler: onChangeHandler,
        isLoading: isLoading,
        onFocus: onFocus,
        onBlur: onBlur,
        autoFocus: props.autoFocus,
        disabled: props.disabled,
        onClearSearch: onClearSearch,
        inputProps: inputProps,
        children: props.children,
      })
    ) : (
      <SearchInput
        searchText={searchText}
        searchResults={searchResults}
        inputPlaceholder={inputPlaceholder}
        onChangeHandler={onChangeHandler}
        isLoading={isLoading}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={props.autoFocus}
        disabled={props.disabled}
        onClearSearch={onClearSearch}
        inputProps={inputProps}
      >
        {props.children}
      </SearchInput>
    );

  const shouldRenderNoResults = () =>
    (searchText.length >= MIN_SEARCH_TEXT_LENGTH ||
      (props.searchFilters?.length && props.showResultsOnLoad)) &&
    !isLoading &&
    !searchResults.length;

  const renderNoResultsDialog = () => {
    const more = !!props.excludeElements?.length ? 'more ' : '';
    const filtersAdvice = !!props.searchFilters?.length
      ? ' You can try by switching the selected filters.'
      : '';
    return (
      <div className="w-full rounded-lg border border-solid border-neutral-200 p-5">
        {!!searchText ? (
          <span>
            {`No ${more}results for "`}
            <i>{searchText}</i>
            {`" were found.${filtersAdvice}`}
          </span>
        ) : (
          <span>{`No ${more}results were found.${filtersAdvice}`}</span>
        )}
      </div>
    );
  };

  const renderResults = () =>
    shouldRenderNoResults() ? (
      renderNoResultsDialog()
    ) : (
      <SearchResultsList
        searchResults={searchResults}
        renderSearchResult={props.renderSearchResult}
        renderLoadingResult={props.renderLoadingResult}
        isLoading={isLoading && !isLoadingNextPage}
        isLoadingNextPage={isLoadingNextPage}
        getOnClickHandler={getOnClickHandler}
      />
    );

  if (!isClientInitialized) {
    return null;
  }

  const searchInputClassNames = 'isd-search-input';
  const searchResultsListClassNames =
    'isd-search-results max-h-96 w-full max-w-none overflow-y-scroll overflow-x-hidden rounded-lg border bg-white text-neutral-700';

  return (
    <div className="isd-search-form w-full" ref={containerRef}>
      {props.renderResultsInOverlay !== false ? (
        <Autosuggest
          alwaysRenderSuggestions={!!props.alwaysRenderResults && !!searchText}
          suggestions={
            shouldRenderNoResults()
              ? (noSearchResults as unknown as T[])
              : searchResults
          }
          onSuggestionsFetchRequested={({ value }) => {
            onChangeSearchText(value);
          }}
          onSuggestionsClearRequested={() => {
            // onSuggestionsClearRequested is required but we don't want to
            // clear search text on select result so this function is noop.
            return;
          }}
          getSuggestionValue={(suggestion) => {
            // We don't want to change the search text when suggestion is highlighted
            // so return current search text as value for all suggestions
            return inputText.current;
          }}
          highlightFirstSuggestion={shouldHighlightFirstSuggestion}
          renderSuggestion={(searchResult, { isHighlighted }) => {
            if (searchResult.objectID === null) {
              return renderNoResultsDialog();
            }
            const index = searchResults.indexOf(searchResult);
            return !!props.renderSearchResult ? (
              props.renderSearchResult({
                key: searchResult.objectID,
                hit: searchResult,
                onClick: getOnClickHandler(searchResult, index),
                isHighlighted,
              })
            ) : (
              <OperatorHit
                key={searchResult.objectID}
                hit={searchResult as unknown as OperatorTruckIndexRecord}
                onClick={getOnClickHandler(searchResult, index)}
                isHighlighted={isHighlighted}
              />
            );
          }}
          renderSuggestionsContainer={({ containerProps, children }) => {
            const style: React.CSSProperties = !!props.resultsOverlayMaxHeight
              ? {
                  maxHeight: props.resultsOverlayMaxHeight,
                  overflowY: 'scroll',
                  overflowX: 'hidden',
                }
              : {};
            return (
              <div {...containerProps} style={style} onScroll={onScroll}>
                {children}
              </div>
            );
          }}
          onSuggestionHighlighted={({ suggestion }: { suggestion: T }) => {
            // Load next page of results as soon as one of the last suggestions gets highlighted
            const index = searchResults.indexOf(suggestion);
            const shouldLoadNextPage = index >= searchResults.length - 4;
            if (shouldLoadNextPage) {
              loadNextPageResults();
            }
          }}
          inputProps={{
            value: searchText,
            onChange: (ev) => {
              // Do not perform any operation on input change
              // unless it want to clear the search text.
              const target = ev.target as HTMLInputElement;
              if (target.value === '') {
                onClearSearch();
              }
            },
            onFocus: onFocus,
            onBlur: onBlur,
          }}
          onSuggestionSelected={(
            event,
            { suggestion, method, suggestionIndex }
          ) => {
            // Simulate a click over suggestion item when
            // enter key is pressed, otherwise let onClick
            // handler manage the suggestion selection.
            if (method !== 'enter') return;
            getOnClickHandler(suggestion, suggestionIndex)();
            // Auto select search text on enter
            selectSearchText(event);
          }}
          renderInputComponent={(inputProps) => {
            return renderInput(inputProps);
          }}
          focusInputOnSuggestionClick={false}
        />
      ) : (
        <>
          <div className={searchInputClassNames}>{renderInput()}</div>
          {!props.hideSearchResults && (
            <div className={searchResultsListClassNames} onScroll={onScroll}>
              {renderResults()}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const SearchFormForwarded = typedForwardRef(SearchForm);

export default SearchFormForwarded;
